import {THREE} from '../three-defs.js';

import * as shaders from '../../game/render/shaders.js';

import * as entity from "../entity.js";

import * as terrain_component from './terrain-component.js';
import * as math from '../math.js';
import * as quality from '../quality.js';



class InstancedFloat16BufferAttribute extends THREE.InstancedBufferAttribute {

	constructor( array, itemSize, normalized, meshPerAttribute = 1 ) {

		super( new Uint16Array( array ), itemSize, normalized, meshPerAttribute );

		this.isFloat16BufferAttribute = true;
	}
};

const M_TMP = new THREE.Matrix4();
const AABB_TMP = new THREE.Box3();
const FRUSTUM_TMP = new THREE.Frustum();
const CELL_TMP = new THREE.Vector3();
const CELL_SIZE_TMP = new THREE.Vector3();
const CAMERA_XZ_TMP = new THREE.Vector3();
const BASE_CELL_TMP = new THREE.Vector3();


// Instance density scales down on mobile (quality.js): with the same
// patch size, half the blades per patch.
const NUM_GRASS = Math.floor((32 * 32) * 3 * quality.GRASS_DENSITY_SCALE);
const GRASS_SEGMENTS_LOW = 1;
const GRASS_SEGMENTS_HIGH = 6;
const GRASS_VERTICES_LOW = (GRASS_SEGMENTS_LOW + 1) * 2;
const GRASS_VERTICES_HIGH = (GRASS_SEGMENTS_HIGH + 1) * 2;
const GRASS_LOD_DIST = 15;
// Three tiers: high-detail blades to 15, the low pool to 125, then a
// sparse far pool (half the instances, 1.75x blade width - same visual
// coverage, half the vertices) carries real blades to 225. All tiers
// share the same fade curve (grassDraw = 15..225), so height and width
// are continuous across pool handoffs - the only per-tier differences
// are density and width, imperceptible at 125m. The +-23-cell iteration
// window supports the 225m reach.
const GRASS_LOW_DIST = 125;
// 225 on desktop, shortened on mobile (quality.js) - the far fade curve
// is shared by every tier, so the reach shrinks without a visible seam.
const GRASS_MAX_DIST = quality.GRASS_MAX_DIST;
const NUM_GRASS_FAR = Math.floor(NUM_GRASS / 2);
const GRASS_FAR_WIDTH_MULT = 1.75;

const GRASS_PATCH_SIZE = 5 * 2;

// Cell iteration window sized to the actual reach (+-23 cells covers the
// desktop 225m; the mobile 150m only needs +-16).
const GRASS_CELL_RANGE = Math.ceil(GRASS_MAX_DIST / GRASS_PATCH_SIZE) + 1;

const GRASS_WIDTH = 0.1;
const GRASS_HEIGHT = 1.5;



export class GrassComponent extends entity.Component {
  static CLASS_NAME = 'GrassComponent';

  get NAME() {
    return GrassComponent.CLASS_NAME;
  }

  #params_;
  #meshesLow_;
  #meshesHigh_;
  #meshesFar_;
  #group_;
  #totalTime_;
  #grassMaterialLow_;
  #grassMaterialHigh_;
  #grassMaterialFar_;
  #geometryLow_;
  #geometryHigh_;
  #geometryFar_;
  #cellHeights_;

  constructor(params) {
    super();

    this.#params_ = params;
    this.#meshesLow_ = [];
    this.#meshesHigh_ = [];
    this.#meshesFar_ = [];
    this.#cellHeights_ = new Map();
    this.#group_ = new THREE.Group();
    this.#group_.name = "GRASS";
    this.#totalTime_ = 0;
    this.#grassMaterialLow_ = null;
    this.#grassMaterialHigh_ = null;
    this.#grassMaterialFar_ = null;
    this.#geometryLow_ = null;
    this.#geometryHigh_ = null;
    this.#geometryFar_ = null;
  }

  Destroy() {
    for (let m of this.#meshesLow_) {
      m.removeFromParent();
    }
    for (let m of this.#meshesHigh_) {
      m.removeFromParent();
    }
    for (let m of this.#meshesFar_) {
      m.removeFromParent();
    }
    this.#group_.removeFromParent();
  }

  #CreateGeometry_(segments) {
    math.set_seed(0);

    const VERTICES = (segments + 1) * 2;

    const indices = [];
    for (let i = 0; i < segments; ++i) {
      const vi = i * 2;
      indices[i*12+0] = vi + 0;
      indices[i*12+1] = vi + 1;
      indices[i*12+2] = vi + 2;

      indices[i*12+3] = vi + 2;
      indices[i*12+4] = vi + 1;
      indices[i*12+5] = vi + 3;

      const fi = VERTICES + vi;
      indices[i*12+6] = fi + 2;
      indices[i*12+7] = fi + 1;
      indices[i*12+8] = fi + 0;

      indices[i*12+9]  = fi + 3;
      indices[i*12+10] = fi + 1;
      indices[i*12+11] = fi + 2;
    }

    const offsets = [];
    for (let i = 0; i < NUM_GRASS; ++i) {
      offsets.push(math.rand_range(-GRASS_PATCH_SIZE * 0.5, GRASS_PATCH_SIZE * 0.5));
      offsets.push(math.rand_range(-GRASS_PATCH_SIZE * 0.5, GRASS_PATCH_SIZE * 0.5));
      offsets.push(0);
    }

    const offsetsData = offsets.map(THREE.DataUtils.toHalfFloat);

    const vertID = new Uint8Array(VERTICES*2);
    for (let i = 0; i < VERTICES*2; ++i) {
      vertID[i] = i;
    }

    const geo = new THREE.InstancedBufferGeometry();
    geo.instanceCount = NUM_GRASS;
    geo.setAttribute('vertIndex', new THREE.Uint8BufferAttribute(vertID, 1));
    geo.setAttribute('position', new InstancedFloat16BufferAttribute(offsetsData, 3));
    geo.setIndex(indices);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1 + GRASS_PATCH_SIZE * 2);

    return geo;
  }

  InitEntity() {
    const threejs = this.FindEntity('threejs').GetComponent('ThreeJSController');

    this.#grassMaterialLow_ = new shaders.GameMaterial('GRASS');
    this.#grassMaterialHigh_ = new shaders.GameMaterial('GRASS');
    this.#grassMaterialLow_.side = THREE.FrontSide;
    this.#grassMaterialHigh_.side = THREE.FrontSide;

    // Placeholder until the cloud noise texture is generated; the compiled
    // program references the sampler, so it must always have a binding.
    const cloudPlaceholder = new THREE.Data3DTexture(
        new Uint8Array([0, 0, 0, 255]), 1, 1, 1);
    cloudPlaceholder.format = THREE.RGBAFormat;
    cloudPlaceholder.needsUpdate = true;
    // Atmosphere defaults (night) until the LUTs are ready - the fog in
    // sky.glsl samples these, so they must be bound from first compile.
    const atmoPlaceholder = new THREE.DataTexture(
        new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat);
    atmoPlaceholder.needsUpdate = true;
    const atmoSunDirDefault = new THREE.Vector3(0, -1, 0);

    this.#grassMaterialFar_ = new shaders.GameMaterial('GRASS');
    this.#grassMaterialFar_.side = THREE.FrontSide;

    for (const m of [this.#grassMaterialLow_, this.#grassMaterialHigh_,
                     this.#grassMaterialFar_]) {
      m.setTexture('cloudShadowNoise', cloudPlaceholder);
      m.setFloat('cloudShadowTime', 0);
      m.setFloat('cloudShadowReady', 0);
      // Shared Vector2 owned by the terrain: mutated in place when the
      // sliding height window re-centres.
      m.setVec2('heightmapOrigin', this.#params_.heightmapOrigin);
      m.setTexture('uAtmoSkyViewLUT', atmoPlaceholder);
      m.setVec3('uAtmoSunDir', atmoSunDirDefault);
      m.setFloat('uAtmoDayFactor', 0);
      m.setFloat('uAtmoViewHeight', 6360.002);
      m.setVec3('uMoonDir', new THREE.Vector3(-1, 0.45, 1).normalize());
      m.setVec3('uShadowLightDir', new THREE.Vector3(-1, 0.45, 1).normalize());
      m.setTexture('heightmap', this.#params_.heightmap);
      m.setVec4('heightParams', new THREE.Vector4(this.#params_.dims, 0, 0, 0));
      // Identical fade curve on every tier: height/width taper is
      // continuous across pool handoffs. grassDraw.z = 1 marks the far
      // tier (skips the per-vertex gust fetch).
      m.setVec4('grassDraw', new THREE.Vector4(
          GRASS_LOD_DIST, GRASS_MAX_DIST, 0, 0));
      m.alphaTest = 0.5;
    }

    this.#geometryLow_ = this.#CreateGeometry_(GRASS_SEGMENTS_LOW);
    this.#geometryHigh_ = this.#CreateGeometry_(GRASS_SEGMENTS_HIGH);
    this.#geometryFar_ = this.#CreateGeometry_(GRASS_SEGMENTS_LOW);
    this.#geometryFar_.instanceCount = NUM_GRASS_FAR;

    this.#grassMaterialLow_.setVec2('grassSize', new THREE.Vector2(GRASS_WIDTH, GRASS_HEIGHT));
    this.#grassMaterialLow_.setVec4('grassParams', new THREE.Vector4(
        GRASS_SEGMENTS_LOW, GRASS_VERTICES_LOW, this.#params_.height, this.#params_.offset));
    this.#grassMaterialLow_.setVec3('grassLODColour', new THREE.Vector3(0, 0, 1));

    this.#grassMaterialHigh_.setVec2('grassSize', new THREE.Vector2(GRASS_WIDTH, GRASS_HEIGHT));
    this.#grassMaterialHigh_.setVec4('grassParams', new THREE.Vector4(
        GRASS_SEGMENTS_HIGH, GRASS_VERTICES_HIGH, this.#params_.height, this.#params_.offset));
    this.#grassMaterialHigh_.setVec3('grassLODColour', new THREE.Vector3(1, 0, 0));

    this.#grassMaterialFar_.setVec2('grassSize', new THREE.Vector2(
        GRASS_WIDTH * GRASS_FAR_WIDTH_MULT, GRASS_HEIGHT));
    this.#grassMaterialFar_.setVec4('grassParams', new THREE.Vector4(
        GRASS_SEGMENTS_LOW, GRASS_VERTICES_LOW, this.#params_.height, this.#params_.offset));
    this.#grassMaterialFar_.setVec4('grassDraw', new THREE.Vector4(
        GRASS_LOD_DIST, GRASS_MAX_DIST, 1, 0));
    this.#grassMaterialFar_.setVec3('grassLODColour', new THREE.Vector3(0, 1, 0));

    threejs.AddSceneObject(this.#group_);
  }

  #CreateMesh_(distToCell) {
    let meshes, geo, mat;
    if (distToCell > GRASS_LOW_DIST) {
      meshes = this.#meshesFar_;
      geo = this.#geometryFar_;
      mat = this.#grassMaterialFar_;
    } else if (distToCell > GRASS_LOD_DIST) {
      meshes = this.#meshesLow_;
      geo = this.#geometryLow_;
      mat = this.#grassMaterialLow_;
    } else {
      meshes = this.#meshesHigh_;
      geo = this.#geometryHigh_;
      mat = this.#grassMaterialHigh_;
    }
    if (meshes.length > 1400) {
      console.log('crap')
      return null;
    }

    const m = new THREE.Mesh(geo, mat);
    m.position.set(0, 0, 0);
    m.receiveShadow = true;
    m.castShadow = false;
    m.visible = false;
    // Blades are displaced onto the terrain in the vertex shader, so the
    // shared geometry's y=0 bounding sphere is wrong wherever the ground
    // is far from zero - three's own frustum test would cull visible
    // patches (square holes when looking down). Update() already culls
    // patches manually with a full-height AABB.
    m.frustumCulled = false;

    meshes.push(m);
    this.#group_.add(m);
    return m;
  }

  Update(timeElapsed) {
    this.#totalTime_ += timeElapsed;

    this.#grassMaterialLow_.setFloat('time', this.#totalTime_);
    this.#grassMaterialHigh_.setFloat('time', this.#totalTime_);
    this.#grassMaterialFar_.setFloat('time', this.#totalTime_);

    const threejs = this.FindEntity('threejs').GetComponent('ThreeJSController');

    const cloudShadow = threejs.cloudShadow_;
    if (cloudShadow && cloudShadow.texture) {
      for (const m of [this.#grassMaterialLow_, this.#grassMaterialHigh_,
                       this.#grassMaterialFar_]) {
        m.setTexture('cloudShadowNoise', cloudShadow.texture);
        m.setFloat('cloudShadowTime', cloudShadow.time);
        m.setFloat('cloudShadowReady', 1);
      }
    }

    const atmo = threejs.atmosphereState_;
    if (atmo && atmo.skyViewTexture) {
      for (const m of [this.#grassMaterialLow_, this.#grassMaterialHigh_,
                       this.#grassMaterialFar_]) {
        m.setTexture('uAtmoSkyViewLUT', atmo.skyViewTexture);
        m.setVec3('uAtmoSunDir', atmo.sunDir);
        m.setFloat('uAtmoDayFactor', atmo.dayFactor);
        m.setFloat('uAtmoViewHeight', atmo.viewHeightKm);
        m.setVec3('uMoonDir', atmo.moonDir);
        m.setVec3('uShadowLightDir', atmo.lightDir);
      }
    }
    const camera = threejs.Camera;
    // The renderer refreshes matrixWorldInverse only at render time, after
    // this update - without this, patches are culled against last frame's
    // camera, and fast mouse flicks (several degrees per frame) blink out
    // a whole strip of patches along the leading screen edge.
    camera.updateMatrixWorld();
    const frustum = FRUSTUM_TMP.setFromProjectionMatrix(
        M_TMP.copy(camera.projectionMatrix).multiply(camera.matrixWorldInverse));

    BASE_CELL_TMP.copy(camera.position);
    BASE_CELL_TMP.divideScalar(GRASS_PATCH_SIZE);
    BASE_CELL_TMP.floor();
    BASE_CELL_TMP.multiplyScalar(GRASS_PATCH_SIZE);

    const terrain = this.#params_.terrain.GetComponent(terrain_component.TerrainComponent.CLASS_NAME);

    CAMERA_XZ_TMP.set(camera.position.x, 0, camera.position.z);
    const playerPos = this.FindEntity('player').Position;

    for (const m of [this.#grassMaterialLow_, this.#grassMaterialHigh_,
                     this.#grassMaterialFar_]) {
      m.setVec3('playerPos', playerPos);
      m.setMatrix('viewMatrixInverse', camera.matrixWorld);
    }

    CELL_SIZE_TMP.set(GRASS_PATCH_SIZE, 1000, GRASS_PATCH_SIZE);

    let lowUsed = 0;
    let highUsed = 0;
    let farUsed = 0;

    for (let x = -GRASS_CELL_RANGE; x < GRASS_CELL_RANGE; x++) {
      for (let z = -GRASS_CELL_RANGE; z < GRASS_CELL_RANGE; z++) {
        const cx = BASE_CELL_TMP.x + x * GRASS_PATCH_SIZE;
        const cz = BASE_CELL_TMP.z + z * GRASS_PATCH_SIZE;

        // The terrain is static, so cell heights are cached permanently.
        const key = cx * 1000000 + cz;
        let cellY = this.#cellHeights_.get(key);
        if (cellY === undefined) {
          cellY = terrain.GetHeight(cx, cz);
          this.#cellHeights_.set(key, cellY);
        }

        CELL_TMP.set(cx, cellY, cz);
        AABB_TMP.setFromCenterAndSize(CELL_TMP, CELL_SIZE_TMP);

        const distToCell = AABB_TMP.distanceToPoint(CAMERA_XZ_TMP);
        if (distToCell > GRASS_MAX_DIST) {
          continue;
        }

        if (!frustum.intersectsBox(AABB_TMP)) {
          continue;
        }

        let m;
        if (distToCell > GRASS_LOW_DIST) {
          m = farUsed < this.#meshesFar_.length ?
              this.#meshesFar_[farUsed] : this.#CreateMesh_(distToCell);
          farUsed++;
        } else if (distToCell > GRASS_LOD_DIST) {
          m = lowUsed < this.#meshesLow_.length ?
              this.#meshesLow_[lowUsed] : this.#CreateMesh_(distToCell);
          lowUsed++;
        } else {
          m = highUsed < this.#meshesHigh_.length ?
              this.#meshesHigh_[highUsed] : this.#CreateMesh_(distToCell);
          highUsed++;
        }
        m.position.set(cx, 0, cz);
        m.visible = true;
      }
    }

    for (let i = lowUsed; i < this.#meshesLow_.length; i++) {
      this.#meshesLow_[i].visible = false;
    }
    for (let i = highUsed; i < this.#meshesHigh_.length; i++) {
      this.#meshesHigh_[i].visible = false;
    }
    for (let i = farUsed; i < this.#meshesFar_.length; i++) {
      this.#meshesFar_[i].visible = false;
    }
  }
}